import urlModule from 'url';
import { Location, Update } from 'history';
import axios from 'axios';
import { Composition } from '../../types/api/Composition';
import { getScrollPosition } from '../../hooks/useScrollPosition';
import { GlobalDocumentIds } from '../../types/GlobalDocumentIds';
import { SectionTypes } from '../../types/api/Sections';

export type State = Update;
export type RefetchOption = 'ignoreQuery' | 'force';

export type LocationStateDefined = Location & {
  state?: {
    refetch?: RefetchOption;
    omitSection?: string[];
  };
};

export const PUBLIC_SESSION_KEY_HEADER = 'x-son-public-ssn-key'; // see in api-gateway ExpiredSessionCheck.ts

export const fetchComposition = async (url: string, previousPublicSessionKey?: string) => {
  return axios.get(url, {
    headers: previousPublicSessionKey ? { [PUBLIC_SESSION_KEY_HEADER]: previousPublicSessionKey } : undefined,
    // errors may result in a JSON for the error page,
    // but with a corresponding status
    validateStatus: () => true,
  });
};

type LocationAndPrevious = {
  location: LocationStateDefined;
  prevLocation?: LocationStateDefined;
};

const isNewUrl = ({ location, prevLocation }: LocationAndPrevious): boolean => {
  return prevLocation ? prevLocation.pathname + prevLocation.search !== location.pathname + location.search : true;
};

/**
 * checking for key to avoid double refetching when the app rerenders
 */
const isForceReFetch = ({ location, prevLocation }: LocationAndPrevious) => {
  return prevLocation ? prevLocation.key !== location.key && location.state?.refetch === 'force' : true;
};

const isSkipReFetchData = ({ location, prevLocation }: LocationAndPrevious): boolean => {
  return location?.state?.refetch === 'ignoreQuery' && location.pathname === prevLocation?.pathname;
};

/**
 * only consider new navigation entries when determining whether we have
 * forceRefetchData or want to refresh a page
 * only refresh if the hashes are equal. If not, we already have all
 * the data we need
 */
const isSamePage = (prevLocation: LocationStateDefined, location: LocationStateDefined) => {
  const isKeyDifferent = location.key !== prevLocation.key;
  return isKeyDifferent && !isNewUrl({ prevLocation, location }) && location.hash === prevLocation.hash;
};

export function needsReFetch(location: LocationStateDefined, prevLocation?: LocationStateDefined) {
  if (isSkipReFetchData({ prevLocation, location })) {
    return false;
  }

  const pathNameAndSearch = prevLocation && prevLocation.pathname + prevLocation.search;

  return (
    pathNameAndSearch &&
    (isForceReFetch({ prevLocation, location }) ||
      isSamePage(prevLocation, location) ||
      isNewUrl({ prevLocation, location }))
  );
}

const getOmittedSections = (location: LocationStateDefined, composition: Composition) => {
  const sectionsToBeOmitted = [];
  if (composition.template !== 'Frameless') {
    sectionsToBeOmitted.push(SectionTypes.personal);
    sectionsToBeOmitted.push(SectionTypes.footer);
  }

  if (location.state && location.state.omitSection) {
    location.state.omitSection.forEach((section: string) => sectionsToBeOmitted.push(section));
  }
  return sectionsToBeOmitted;
};

export const getFetchUrl = (location: LocationStateDefined, composition: Composition) => {
  const fetchUrl = urlModule.parse(`/json${location.pathname + location.search}`, true);
  const sectionsToBeOmitted = getOmittedSections(location, composition);
  if (sectionsToBeOmitted.length) {
    fetchUrl.search = null;
    fetchUrl.query.omitSection = sectionsToBeOmitted;
  }
  return urlModule.format(fetchUrl);
};

/**
 * As our SPA navigation is not optimal, we need to reimplement the scroll restoration feature.
 * see: SON-11194
 * @param loc location of the new page
 */
export const handleScrollPositionAfterPageNavigation = (loc: Location) => {
  const sessionTopOrNot = getScrollPosition(loc);
  const contentElement = document.getElementById(GlobalDocumentIds.scrollContent);
  contentElement?.scrollTo({ top: sessionTopOrNot ? sessionTopOrNot.top : 0 });
};

/**
 * seperate to scroll position, we have to handle where focus will land after navigation.
 * usually its the h1, as per w3c standard.
 * expections:
 *  - h2 of profile edit page, else users will not notice our hint which is above our h1
 */
export const handleFocusAfterPageNavigation = () => {
  const personProfileEditHint: HTMLHeadingElement | null = document.querySelector(
    'h2[id^="widget-header-personprofileedithint-"]',
  );
  if (personProfileEditHint) {
    personProfileEditHint.setAttribute('tabindex', '-1');
    personProfileEditHint.focus({ preventScroll: true });
  } else {
    document.getElementById(GlobalDocumentIds.pageTitle)?.focus({ preventScroll: true });
  }
};
